import { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../authentication/AuthContext";
import Layout from "../shared/Layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { logAnalyticsEvent } from "../engagement/Amplitude";
import MDEditor from "@uiw/react-md-editor";
import {
  useShowTempErrorMessage,
  useShowTempMessage,
} from "../shared/hooks/useUtilityContext";
import { useTask } from "./hooks/useTask";
import { editTask } from "./utils/TaskUtils";
import GetPromptButton from "./components/GetPromptButton";
import React from "react";

const EditTask = () => {
  const { id } = useParams();

  const { data: task, isLoading, isError, error } = useTask(parseInt(id!));

  const [title, setTitle] = useState("");
  const [initialDescription, setInitialDescription] = useState("");
  const [description, setDescription] = useState("");
  const [snippet, setSnippet] = useState("");
  const [textCount, setTextCount] = useState(0);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    if (task) {
      setTags(task.tags);
      setTitle(task.title);
      setDescription(task.description);
      setSnippet(task.snippet);
      setInitialDescription(task.description);
      setTextCount((task.description ?? "").length);
    }
  }, [task]);

  const { user, setUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const showTempMessage = useShowTempMessage();
  const showTempErrorMessage = useShowTempErrorMessage();
  const [isModified, setIsModified] = useState(false);
  const snippetRef = React.createRef<HTMLTextAreaElement>();

  useEffect(() => {
    // Load form data from local storage when the component mounts
    const savedTitle = localStorage.getItem("taskTitle");
    const savedDescription = localStorage.getItem("taskDescription");
    const savedTags = localStorage.getItem("taskTags");
    const savedSnippet = localStorage.getItem("taskSnippet");

    if (savedTitle) {
      setTitle(savedTitle);
    }
    if (savedDescription) {
      setDescription(savedDescription);
    }
    if (savedTags && JSON.parse(savedTags).length > 0) {
      setTags(JSON.parse(savedTags));
    }
  }, []);

  useEffect(() => {
    // Save form data to local storage whenever it changes
    localStorage.setItem("taskTitle", title);
    localStorage.setItem("taskDescription", description);
    localStorage.setItem("taskTags", JSON.stringify(tags));
  }, [title, description, tags]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (submitting) {
      return;
    }

    logAnalyticsEvent("Press Submit Guide", {
      method: "edit",
    });

    try {
      const data = await editTask({
        task: task!,
        title,
        description,
        tags,
        snippet,
        user: user!,
        setSubmitting,
      });

      console.log("Task submitted successfully");
      // Reset form fields after successful submission
      setTitle("");
      setDescription("");
      setTags([]);
      localStorage.removeItem("taskTitle");
      localStorage.removeItem("taskDescription");
      localStorage.removeItem("taskTags");
      localStorage.removeItem("taskSnippet");
      navigate(`/snippet/${data!.id}`);
    } catch (error) {
      showTempErrorMessage("Error submitting task. Please try again later.");
      setSubmitting(false);
      console.error("Error submitting task:", error);
    }
  };

  const handleInput = () => {
    const textarea = snippetRef.current;

    if (!textarea) return;
    const maxHeight = 1000; // Set your desired max height here

    textarea.style.height = "auto";
    const newHeight = textarea.scrollHeight;
    if (newHeight > maxHeight) {
      textarea.style.height = maxHeight + "px";
      textarea.style.overflowY = "auto"; // Enable scrolling
    } else {
      textarea.style.height = newHeight + "px";
    }
  };

  useEffect(() => {
    handleInput();
  }, [snippet]);

  return (
    <Layout>
      {isLoading && <div className="loading loading-lg"></div>}
      {isError && (
        <div className="text-lg text-gray-500 text-center mt-8">
          Error loading task
        </div>
      )}
      {!isLoading && !isError && (
        <div className="container mx-auto">
          <form
            onSubmit={handleSubmit}
            className="mx-4 mt-4"
          >
            <div className="form-control mb-4">
              <div className="text-md font-bold mt-4">Edit Snippet</div>

              <textarea
                ref={snippetRef}
                className="textarea textarea-bordered mt-2 w-full leading-5 font-mono"
                value={snippet}
                onChange={(e) => setSnippet(e.target.value)}
                placeholder="Enter your code snippet here"
              ></textarea>
              <div className="text-md font-bold mt-8">Snippet Title</div>

              <span className="prose text-sm font-light">
                Be specific and imagine you’re asking a question to another
                person
              </span>
              <input
                type="text"
                placeholder="Enter the guide title"
                className="input input-bordered mt-2"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end w-full mb-4">
              <GetPromptButton title={title} />
            </div>
            <div className="form-control mb-4">
              <div className="text-md font-bold mt-8">
                Snippet Description (optional)
              </div>
              <textarea
                placeholder="Description"
                value={description}
                maxLength={2000}
                onChange={(e) => setDescription(e.target.value)}
                className="textarea textarea-bordered mt-2 w-full"
              ></textarea>
              {/* <MDEditor
                //ref={descriptionRef}
                //placeholder="Enter the task steps"
                //rows={5}
                //maxLength={3000}
                className=""
                height={"80vh"}
                minHeight={300}
                visibleDragbar={false}
                maxHeight={800}
                value={description}
                onChange={(e) => {
                  setDescription(e ?? "");
                  setIsModified(e !== initialDescription);
                  setTextCount((e ?? "").length);
                }}
              ></MDEditor> */}
              {/* <div className="flex justify-end items-center mt-2">
                <progress
                  className="progress w-56 mr-4"
                  value={(textCount / 3000) * 100}
                  max="100"
                ></progress>
                <p className="text-sm text-end">{textCount}/3000</p>
              </div> */}
            </div>
            <div className="form-control mb-8">
              <span className="text-md font-bold">
                <span>Tags</span>
              </span>
              <label className="text-sm font-light">
                Tags help others find your guide. Separate tags with commas.
              </label>
              <input
                type="text"
                placeholder="Enter tags (comma-separated)"
                className="input input-bordered mt-2"
                value={tags}
                onChange={(e) =>
                  setTags(
                    e.target.value
                      .split(",")
                      .map((tag) => tag.toLowerCase().trim().replace(" ", "-"))
                  )
                }
              />
            </div>
            <div className="flex items-center gap-4 mb-8">
              <button
                type="submit"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                Save Edit
              </button>
              <button
                type="submit"
                className="btn btn-outline"
                onClick={() => {
                  navigate(`/snippet/${id}`);
                }}
              >
                Cancel
              </button>
              {submitting && (
                <div className="loading loading-spinner ml-4"></div>
              )}
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};

export default EditTask;
